import type { Ref } from 'vue';
import type { FormBlockContext, FormSaveError, FormSaveHandler } from '#layers/forms/public';
import { tryOnScopeDispose } from '@vueuse/core';
import { ref } from 'vue';

export interface UseFormBlockSaveHandlersReturn {
	saveHandlerErrors: Ref<FormSaveError[]>;
	onSave: FormBlockContext['onSave'];
	captureSaveHandlers: () => readonly FormSaveHandler[];
	reportHasChanges: (getter: () => boolean) => void;
	hasExternalChanges: () => boolean;
	clearSaveHandlerErrors: () => void;
}

export function useFormBlockSaveHandlers(): UseFormBlockSaveHandlersReturn {
	const saveHandlers = new Set<() => FormSaveHandler>();
	const hasChangesReporters = ref<Set<() => boolean>>(new Set());
	const saveHandlerErrors = ref<FormSaveError[]>([]);

	function onSave(capture: () => FormSaveHandler) {
		saveHandlers.add(capture);
		tryOnScopeDispose(() => saveHandlers.delete(capture));
	}

	function captureSaveHandlers(): readonly FormSaveHandler[] {
		return [...saveHandlers].map((capture) => capture());
	}

	function reportHasChanges(getter: () => boolean) {
		hasChangesReporters.value.add(getter);
		tryOnScopeDispose(() => hasChangesReporters.value.delete(getter));
	}

	function hasExternalChanges(): boolean {
		for (const getter of hasChangesReporters.value) {
			if (getter()) return true;
		}

		return false;
	}

	function clearSaveHandlerErrors() {
		saveHandlerErrors.value = [];
	}

	return {
		saveHandlerErrors,
		onSave,
		captureSaveHandlers,
		reportHasChanges,
		hasExternalChanges,
		clearSaveHandlerErrors,
	};
}
